// The signed-in user's settings, as a runtime Program (runtime/effects.ts).
//
// A setting is a preference the server keeps for the user
// (`/api/me/preferences`, each name at a revision that grows with every
// write), so it is the same in every browser they use. Today there is one:
// whether review marks and GitHub's per-file "Viewed" checkbox follow each
// other (`github_viewed`; state/viewedSync.svelte.ts does the following).
//
// - Never chosen means on. Until the server has answered, the choice is
//   not known, and the sync counts as off: nothing is written to GitHub
//   on a guess.
// - A change shows at once and is written from the revision it was made
//   from. One write is on its way at a time; a change made meanwhile is
//   sent after it, from the revision it produced.
// - A write the server refuses for the revision (another device wrote) is
//   made again on the revision just read, unless that one already says the
//   same: the user's latest word stands.
// - A write that fails is rolled back: the setting shows what the server
//   holds again, with why (`refused`) until the next change.
// - A revision older than the one held is never shown.
//
// Builds that kept the "Viewed" choice in the browser (`legacyOff`): an
// explicit "off" found there is written to the server once, from revision
// 0 only, so a choice already made on any device wins over it; the
// browser's copy is dropped (the `drop-legacy` port effect) once the
// server holds a choice, whoever made it. Until then it counts as off.

import { failureOf, type Failure } from '../../api/failure';
import { port, retry, task, type Effect, type Next, type Outcome, type Scheduled, type Work } from '../../runtime/effects';
import { failed, loading, manual, notAsked, ready, retryAt, settle, type Remote } from '../../runtime/remote';

/** The name of the server preference that holds the "Viewed" choice: `true` or `false`; anything else counts as never chosen. */
export const VIEWED_PREFERENCE = 'github_viewed';

/** What the server holds of the settings. */
export interface ServerSettings {
  /** The "Viewed" sync is on or off; null when the user never chose. */
  viewedSync: boolean | null;
  /** The revision of that choice; 0 when it was never written. */
  rev: number;
}

/** The settings among the server's preferences and their revisions. */
export function serverSettings(preferences: Record<string, unknown>, revisions: Record<string, unknown>): ServerSettings {
  const r = revisions[VIEWED_PREFERENCE];
  const v = preferences[VIEWED_PREFERENCE];
  return { viewedSync: typeof v === 'boolean' ? v : null, rev: typeof r === 'number' && Number.isSafeInteger(r) && r > 0 ? r : 0 };
}

/** What the program asks of the world. */
export interface SettingsWorld {
  read: Work<ServerSettings>;
  /** Make `on` the server's choice, from revision `from`: what it holds then, or `conflict` when it is at another. */
  write: (on: boolean, from: number) => Work<ServerSettings | 'conflict'>;
  /** Whether an earlier build left an explicit "off" in this browser. */
  legacyOff: () => boolean;
  now: () => number;
  random?: () => number;
}

/** A change on its way to the server. */
export interface Change {
  on: boolean;
  /** It is the browser's old "off" being carried, not something the user just chose: its failure is not told, and another device's choice wins over it. */
  carried: boolean;
  /** `reading`: the server refused it for the revision, and is being read. */
  phase: 'writing' | 'reading';
  /** Times the server refused it for the revision. */
  refusals: number;
}

/** The sync's trouble on the pull request last looked at, for the Settings page: GitHub refusing it there, or files whose sync failed. */
export interface ViewedHealth {
  /** `owner/repo#n`. */
  pr: string;
  /** The pull request's Files view (an app path). */
  path: string;
  /** Why GitHub refuses the mutation for that pull request; null when it does not. */
  paused: string | null;
  /** Files there whose sync failed. */
  errors: number;
}

export interface SettingsState {
  server: Remote<ServerSettings>;
  change: Change | null;
  /** The last change did not last: what was asked for, and why not. */
  refused: { wanted: boolean; reason: Failure } | null;
  legacyOff: boolean;
  /** Failed reads in a row. */
  failures: number;
  health: ViewedHealth | null;
}

export type SettingsMsg =
  /** Someone is signed in: read their settings. */
  | { t: 'open' }
  /** The settings may have changed elsewhere: read them again. */
  | { t: 'refresh' }
  | { t: 'read'; o: Outcome<ServerSettings, Failure>; at: number }
  /** The user's choice. */
  | { t: 'set-viewed-sync'; on: boolean }
  | { t: 'wrote'; sent: boolean; o: Outcome<ServerSettings | 'conflict', Failure> }
  | { t: 'retry-armed' | 'retry-lapsed' | 'retry-fire'; s: Scheduled }
  /** How the sync fares on the pull request shown. */
  | { t: 'viewed-health'; health: ViewedHealth }
  /** The refusal was read. */
  | { t: 'dismiss' };

/** What the program hands its owner: forget the choice an earlier build kept in this browser. */
export type SettingsOut = { kind: 'drop-legacy' };

type Fx = Effect<SettingsMsg, SettingsOut>[];
type Step = Next<SettingsState, SettingsMsg, SettingsOut>;

/** Writes refused for the revision before a change is given up (each is made again on the revision just read). */
export const CONFLICTS_MAX = 3;

/**
 * The "Viewed" choice to show: the change on its way, else the server's
 * (never chosen: on, or off while the browser's old "off" waits to be
 * carried); null while the server has not answered.
 */
export function viewedSyncChoice(s: SettingsState): boolean | null {
  if (s.change) return s.change.on;
  if (s.server.tag !== 'Has') return null;
  return s.server.value.viewedSync ?? !s.legacyOff;
}

/** Whether review marks and GitHub's "Viewed" follow each other now: only a known "on". */
export const viewedSyncOn = (s: SettingsState): boolean => viewedSyncChoice(s) === true;

/** Whether the user's own change is on its way (the switch says so). */
export const saving = (s: SettingsState): boolean => !!s.change && !s.change.carried;

/** Why a write failed, in a sentence. */
function why(f: Failure): string {
  switch (f.cause) {
    case 'unreachable':
      return 'Changeling’s server isn’t reachable.';
    case 'offline':
      return 'You’re offline.';
    case 'server_error':
      return 'Changeling’s server ran into a problem.';
    case 'signed_out':
      return 'You’re signed out.';
    case 'rate_limited':
      return 'Too many requests just now; try again in a moment.';
    default:
      return f.server?.text ?? (/[.!?]$/.test(f.message) ? f.message : `${f.message}.`);
  }
}

/** What a refused change is said as: what was asked, what the setting still is, and why. */
export function refusalText(r: { wanted: boolean; reason: Failure }): string {
  return `Couldn’t turn this ${r.wanted ? 'on' : 'off'}, so it is still ${r.wanted ? 'off' : 'on'}. ${why(r.reason)}`;
}

export function settingsProgram(world: SettingsWorld): { init: () => Step; update: (s: SettingsState, msg: SettingsMsg) => Step } {
  const read = (): Fx => [
    task<ServerSettings, Failure, SettingsMsg>(
      'read',
      world.read,
      (o) => ({ t: 'read', o, at: world.now() }),
      (e) => failureOf(e, world.now()),
    ),
  ];
  const write = (on: boolean, from: number): Fx => [
    task<ServerSettings | 'conflict', Failure, SettingsMsg>(
      'write',
      world.write(on, from),
      (o) => ({ t: 'wrote', sent: on, o }),
      (e) => failureOf(e, world.now()),
    ),
  ];
  const dropLegacy = (s: SettingsState): [SettingsState, Fx] => (s.legacyOff ? [{ ...s, legacyOff: false }, [port({ kind: 'drop-legacy' })]] : [s, []]);
  /** The change did not last: the user's is told, the carried one waits for the next read. */
  const giveUp = (s: SettingsState, change: Change, reason: Failure): SettingsState => {
    const back = { ...s, change: null };
    // Nothing to tell when what the server holds is what was last asked for.
    return change.carried || viewedSyncChoice(back) === change.on ? back : { ...back, refused: { wanted: change.on, reason } };
  };

  function onRead(s: SettingsState, msg: Extract<SettingsMsg, { t: 'read' }>): Step {
    const change = s.change;
    if (!msg.o.ok) {
      const reason = msg.o.error;
      if (change?.phase === 'reading') return [giveUp(s, change, reason)];
      // What is shown stays: the next read brings what changed.
      if (s.server.tag === 'Has') return [s];
      const failures = s.failures + 1;
      const at = retryAt(reason, failures - 1, msg.at, world.random);
      const next = { ...s, server: failed(reason, manual), failures };
      if (at === null) return [next];
      return [next, [retry<SettingsMsg>('read-retry', at, { armed: (r) => ({ t: 'retry-armed', s: r }), fire: (r) => ({ t: 'retry-fire', s: r }), lapsed: (r) => ({ t: 'retry-lapsed', s: r }) })]];
    }
    const theirs = msg.o.value;
    // An answer from before a write that has landed since.
    if (s.server.tag === 'Has' && theirs.rev < s.server.value.rev) return [s];
    const held: SettingsState = { ...s, server: ready(theirs), failures: 0 };
    if (change?.phase === 'reading') {
      // Another device wrote. The carried "off" yields to it; the user's own change is made again on it.
      if (change.carried || theirs.viewedSync === change.on) {
        const [next, fx] = theirs.rev > 0 ? dropLegacy(held) : [held, []];
        return [{ ...next, change: null }, fx];
      }
      return [{ ...held, change: { ...change, phase: 'writing' } }, write(change.on, theirs.rev)];
    }
    if (change || !held.legacyOff) return [held];
    // The browser's old "off": carried when the server never held a choice, dropped when it does.
    if (theirs.rev > 0) return dropLegacy(held);
    return [{ ...held, change: { on: false, carried: true, phase: 'writing', refusals: 0 } }, write(false, 0)];
  }

  function onWrote(s: SettingsState, msg: Extract<SettingsMsg, { t: 'wrote' }>): Step {
    const change = s.change;
    if (!change) return [s];
    if (!msg.o.ok) return [giveUp(s, change, msg.o.error)];
    if (msg.o.value === 'conflict') {
      if (change.refusals >= CONFLICTS_MAX) return [giveUp(s, change, failureOf(new Error('This setting kept changing on another device.'), world.now()))];
      return [{ ...s, change: { ...change, phase: 'reading', refusals: change.refusals + 1 } }, read()];
    }
    // The server holds a choice now, so the browser's old one is done with.
    const [held, fx] = dropLegacy({ ...s, server: ready(msg.o.value), failures: 0 });
    if (change.on === msg.sent) return [{ ...held, change: null }, fx];
    // Changed again meanwhile: that is the next write, from the revision this one made.
    return [{ ...held, change: { ...change, phase: 'writing', refusals: 0 } }, [...fx, ...write(change.on, msg.o.value.rev)]];
  }

  return {
    init: () => [{ server: notAsked, change: null, refused: null, legacyOff: world.legacyOff(), failures: 0, health: null }],
    update(s, msg) {
      switch (msg.t) {
        case 'open':
          return s.server.tag === 'NotAsked' ? [{ ...s, server: loading }, read()] : [s];
        case 'refresh':
          // A change on its way brings the server's answer itself; a first read still running is the read.
          if (s.change || s.server.tag === 'NotAsked' || s.server.tag === 'Loading') return [s];
          return [s.server.tag === 'Failed' ? { ...s, server: loading } : s, read()];
        case 'read':
          return onRead(s, msg);
        case 'retry-armed':
          return s.server.tag === 'Failed' ? [{ ...s, server: failed(s.server.reason, msg.s) }] : [s];
        case 'retry-lapsed':
          return [{ ...s, server: settle(s.server, msg.s) }];
        case 'retry-fire':
          return s.server.tag === 'Failed' ? [{ ...s, server: loading }, read()] : [s];
        case 'set-viewed-sync': {
          // Not known yet: there is nothing to change from (the switch is not offered).
          if (s.server.tag !== 'Has') return [s];
          if (msg.on === viewedSyncChoice(s)) return [s.refused ? { ...s, refused: null } : s];
          const heard = { ...s, refused: null };
          // While a write is on its way this one waits for its answer.
          if (s.change) return [{ ...heard, change: { ...s.change, on: msg.on, carried: false } }];
          return [{ ...heard, change: { on: msg.on, carried: false, phase: 'writing', refusals: 0 } }, write(msg.on, s.server.value.rev)];
        }
        case 'wrote':
          return onWrote(s, msg);
        case 'viewed-health': {
          const h = msg.health;
          if (h.paused === null && h.errors === 0) return s.health?.pr === h.pr ? [{ ...s, health: null }] : [s];
          const was = s.health;
          return was && was.pr === h.pr && was.paused === h.paused && was.errors === h.errors ? [s] : [{ ...s, health: h }];
        }
        case 'dismiss':
          return s.refused ? [{ ...s, refused: null }] : [s];
      }
    },
  };
}
